import React, { useState, useEffect } from 'react';
import { Cloud, Droplets, Wind, Thermometer, Sun, CloudRain, CloudSnow, CloudLightning, CloudFog } from 'lucide-react';

const WMO_ICONS = {
  0: Sun, 1: Sun, 2: Cloud, 3: Cloud,
  45: CloudFog, 48: CloudFog,
  51: Droplets, 53: Droplets, 55: Droplets,
  61: CloudRain, 63: CloudRain, 65: CloudRain,
  71: CloudSnow, 73: CloudSnow, 75: CloudSnow, 77: CloudSnow,
  80: CloudRain, 81: CloudRain, 82: CloudRain,
  85: CloudSnow, 86: CloudSnow,
  95: CloudLightning, 96: CloudLightning, 99: CloudLightning,
};

const WMO_LABELS = {
  0: 'Clear', 1: 'Mostly Clear', 2: 'Partly Cloudy', 3: 'Overcast',
  45: 'Foggy', 48: 'Rime Fog',
  51: 'Light Drizzle', 53: 'Drizzle', 55: 'Heavy Drizzle',
  61: 'Light Rain', 63: 'Rain', 65: 'Heavy Rain',
  71: 'Light Snow', 73: 'Snow', 75: 'Heavy Snow', 77: 'Snow Grains',
  80: 'Light Showers', 81: 'Showers', 82: 'Heavy Showers',
  85: 'Snow Showers', 86: 'Heavy Snow Showers',
  95: 'Thunderstorm', 96: 'Hail Storm', 99: 'Heavy Hail',
};

const WeatherWidget = () => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWeather();
  }, []);

  const fetchWeather = async () => {
    try {
      // Open-Meteo free API - no key needed. Regina, SK coords: 50.45, -104.62
      const res = await fetch(
        'https://api.open-meteo.com/v1/forecast?latitude=50.45&longitude=-104.62&current=temperature_2m,relative_humidity_2m,wind_speed_10m,weather_code&temperature_unit=celsius&wind_speed_unit=kmh&timezone=America/Regina'
      );
      const data = await res.json();
      if (data.current) {
        setWeather({
          temp: Math.round(data.current.temperature_2m),
          humidity: data.current.relative_humidity_2m,
          wind: Math.round(data.current.wind_speed_10m),
          code: data.current.weather_code,
        });
      }
    } catch (err) {
      console.error('Weather fetch failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const WeatherIcon = weather ? (WMO_ICONS[weather.code] || Cloud) : Cloud;
  const label = weather ? (WMO_LABELS[weather.code] || 'Unknown') : '';

  return (
    <div className="bg-[#111] border border-[#1a1a1a] rounded p-4 mb-4" data-testid="weather-widget">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-sm text-[#C5A52A] tracking-wider">REGINA WEATHER</h3>
        <Thermometer className="w-4 h-4 text-[#C5A52A]" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-[#C5A52A]" />
        </div>
      ) : weather ? (
        <div>
          <div className="flex items-center gap-3 mb-3">
            <WeatherIcon className="w-10 h-10 text-[#C5A52A]" />
            <div>
              <div className="font-headline text-3xl text-white">{weather.temp}°C</div>
              <div className="font-mono text-xs text-gray-400">{label}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex items-center gap-1.5 text-gray-500">
              <Wind className="w-3 h-3" />
              <span className="font-mono text-[11px]">{weather.wind} km/h</span>
            </div>
            <div className="flex items-center gap-1.5 text-gray-500">
              <Droplets className="w-3 h-3" />
              <span className="font-mono text-[11px]">{weather.humidity}%</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-4">
          <Cloud className="w-6 h-6 text-gray-600 mx-auto mb-1" />
          <p className="font-mono text-xs text-gray-600">Weather unavailable</p>
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-[#1a1a1a]">
        <p className="font-mono text-[10px] text-gray-600 text-center">Regina, Saskatchewan</p>
      </div>
    </div>
  );
};

export default WeatherWidget;
